import { Chip, Grid, Typography, Box, Paper } from '@mui/material'

/**
 * Configuration for rendering specific fields for each AMLDD check type
 */

// PEP (Politically Exposed Person)
export const renderPepFields = (record, t) => (
  <>
    {record.classificacao && (
      <Grid item xs={12}>
        <Chip
          label={record.classificacao === 'T' ? t('common:amldd.holder') : t('common:amldd.related')}
          color={record.classificacao === 'T' ? 'error' : 'warning'}
          size="small"
          sx={{ mt: 1 }}
        />
      </Grid>
    )}

    {record.score !== undefined && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.score')}:</strong> {record.score}
        </Typography>
      </Grid>
    )}

    {record.documentos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.documents')}:</strong> {record.documentos}
        </Typography>
      </Grid>
    )}

    {record.descricao && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.description')}:</strong> {record.descricao}
        </Typography>
      </Grid>
    )}

    {record.informacoesAdicional && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.additionalInfo')}:</strong> {record.informacoesAdicional}
        </Typography>
      </Grid>
    )}

    {record.dataInicio && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.startDate')}:</strong>{' '}
          {new Date(record.dataInicio).toLocaleDateString()}
        </Typography>
      </Grid>
    )}

    {record.dataFim && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.endDate')}:</strong>{' '}
          {new Date(record.dataFim).toLocaleDateString()}
        </Typography>
      </Grid>
    )}

    {record.dataNascimentoFundacao && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.birthDate')}:</strong>{' '}
          {new Date(record.dataNascimentoFundacao).toLocaleDateString()}
        </Typography>
      </Grid>
    )}

    {record.cidadeUfCargo && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.location')}:</strong> {record.cidadeUfCargo}
        </Typography>
      </Grid>
    )}

    {record.oficial && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.official')}:</strong>{' '}
          {record.oficial === 'S' ? t('common:yes') : t('common:no')}
        </Typography>
      </Grid>
    )}

    {record.dataObito && (
      <Grid item xs={12}>
        <Typography variant="subtitle3" color="text.secondary">
          <strong>{t('common:amldd.deathDate')}:</strong>{' '}
          {new Date(record.dataObito).toLocaleDateString()}
        </Typography>
      </Grid>
    )}

    {record.dataAtualizacao && (
      <Grid item xs={12}>
        <Typography variant="caption">
          <strong>{t('common:amldd.lastUpdate')}:</strong>{' '}
          {new Date(record.dataAtualizacao).toLocaleString()}
        </Typography>
      </Grid>
    )}
  </>
)

// ESG (Environmental, Social, Governance)
export const renderEsgFields = (record, t) => (
  <>
    {record.qtdAbonadoras !== undefined && (
      <Grid item xs={12}>
        <Chip
          label={`${t('common:amldd.positive')}: ${record.qtdAbonadoras}`}
          color="success"
          size="small"
          sx={{ mr: 1, mt: 1, color: 'white' }}
        />
      </Grid>
    )}

    {record.qtdDesabonadoras !== undefined && (
      <Grid item xs={12}>
        <Chip
          label={`${t('common:amldd.negative')}: ${record.qtdDesabonadoras}`}
          color="error"
          size="small"
          sx={{ mt: 1, color: 'white' }}
        />
      </Grid>
    )}

    {record.documentos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.documents')}:</strong> {record.documentos}
        </Typography>
      </Grid>
    )}

    {record.perfil && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.profile')}:</strong> {record.perfil}
        </Typography>
      </Grid>
    )}

    {record.enquadramentos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.classifications')}:</strong> {record.enquadramentos}
        </Typography>
      </Grid>
    )}

    {record.pseudonimos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.aliases')}:</strong> {record.pseudonimos}
        </Typography>
      </Grid>
    )}

    {record.titulosDesabonadores && (
      <Grid item xs={12}>
        <Typography variant="subtitle3" color="error">
          <strong>{t('common:amldd.negativeTitles')}:</strong>
        </Typography>
        <Box sx={{ pl: 2 }}>
          {record.titulosDesabonadores.split('|').map((titulo, idx) => (
            <Typography key={idx} variant="body2">• {titulo}</Typography>
          ))}
        </Box>
      </Grid>
    )}

    {record.titulosAbonadores && (
      <Grid item xs={12}>
        <Typography variant="subtitle3" color="success.main">
          <strong>{t('common:amldd.positiveTitles')}:</strong>
        </Typography>
        <Box sx={{ pl: 2 }}>
          {record.titulosAbonadores.split('|').map((titulo, idx) => (
            <Typography key={idx} variant="body2">• {titulo}</Typography>
          ))}
        </Box>
      </Grid>
    )}

    {record.ultimaAtualizacao && (
      <Grid item xs={12}>
        <Typography variant="caption">
          <strong>{t('common:amldd.lastUpdate')}:</strong>{' '}
          {new Date(record.ultimaAtualizacao).toLocaleString()}
        </Typography>
      </Grid>
    )}
  </>
)

// LRI (International Restrictive Lists)
export const renderLriFields = (record, t) => (
  <>
    {record.origem && (
      <Grid item xs={12}>
        <Chip
          label={record.origem}
          color="error"
          size="small"
          sx={{ mt: 1 }}
        />
      </Grid>
    )}

    {record.score !== undefined && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.score')}:</strong> {record.score}
        </Typography>
      </Grid>
    )}

    {record.documentos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.documents')}:</strong> {record.documentos}
        </Typography>
      </Grid>
    )}

    {record.pseudonimos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.aliases')}:</strong> {record.pseudonimos}
        </Typography>
      </Grid>
    )}

    {record.datasNascimento && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.birthDate')}:</strong>{' '}
          {new Date(record.datasNascimento).toLocaleDateString()}
        </Typography>
      </Grid>
    )}

    {record.locaisNascimento && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.birthPlace')}:</strong> {record.locaisNascimento}
        </Typography>
      </Grid>
    )}

    {record.enderecos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.addresses')}:</strong> {record.enderecos}
        </Typography>
      </Grid>
    )}

    {record.programas && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.programs')}:</strong> {record.programas}
        </Typography>
      </Grid>
    )}

    {record.acusacoes && (
      <Grid item xs={12}>
        <Typography variant="subtitle3" color="error">
          <strong>{t('common:amldd.accusations')}:</strong> {record.acusacoes}
        </Typography>
      </Grid>
    )}

    {record.procuradoPor && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.wantedBy')}:</strong> {record.procuradoPor}
        </Typography>
      </Grid>
    )}

    {record.dataEntrada && (
      <Grid item xs={12}>
        <Typography variant="caption">
          <strong>{t('common:amldd.entryDate')}:</strong>{' '}
          {new Date(record.dataEntrada).toLocaleString()}
        </Typography>
      </Grid>
    )}
  </>
)

// MTLSN Simple (Media monitoring - Simple version)
export const renderMtlsnSimpleFields = (record, t) => (
  <>
    {record.citado_midia !== undefined && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.mentionedInMedia')}:</strong>{' '}
          <Chip
            label={record.citado_midia ? t('common:yes') : t('common:no')}
            color={record.citado_midia ? 'error' : 'default'}
            size="small"
          />
        </Typography>
      </Grid>
    )}

    {record.crimes && (
      <Grid item xs={12}>
        <Typography variant="subtitle3" color="error">
          <strong>{t('common:amldd.crimes')}:</strong>
        </Typography>
        <Box sx={{ pl: 2 }}>
          {record.crimes.split('|').map((crime, idx) => (
            <Typography key={idx} variant="body2">• {crime}</Typography>
          ))}
        </Box>
      </Grid>
    )}

    {record.data && (
      <Grid item xs={12}>
        <Typography variant="caption">
          <strong>{t('common:amldd.date')}:</strong>{' '}
          {new Date(record.data).toLocaleString()}
        </Typography>
      </Grid>
    )}
  </>
)

// MTLSN (Media monitoring - Full version)
export const renderMtlsnFields = (record, t) => (
  <>
    {record.score !== undefined && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.score')}:</strong> {record.score}
        </Typography>
      </Grid>
    )}

    {record.citado_midia !== undefined && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.mentionedInMedia')}:</strong>{' '}
          <Chip
            label={record.citado_midia ? t('common:yes') : t('common:no')}
            color={record.citado_midia ? 'error' : 'default'}
            size="small"
          />
        </Typography>
      </Grid>
    )}

    {record.pep !== undefined && (
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.isPep')}:</strong>{' '}
          <Chip
            label={record.pep ? t('common:yes') : t('common:no')}
            color={record.pep ? 'warning' : 'default'}
            size="small"
          />
        </Typography>
      </Grid>
    )}

    {record.documentos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.documents')}:</strong> {record.documentos}
        </Typography>
      </Grid>
    )}

    {record.pseudonimos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.aliases')}:</strong> {record.pseudonimos}
        </Typography>
      </Grid>
    )}

    {record.envolvimentos && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.involvements')}:</strong> {record.envolvimentos}
        </Typography>
      </Grid>
    )}

    {record.crimes && (
      <Grid item xs={12}>
        <Typography variant="subtitle3" color="error">
          <strong>{t('common:amldd.crimes')}:</strong>
        </Typography>
        <Box sx={{ pl: 2 }}>
          {record.crimes.split('|').map((crime, idx) => (
            <Typography key={idx} variant="body2">• {crime}</Typography>
          ))}
        </Box>
      </Grid>
    )}

    {record.listas && (
      <Grid item xs={12}>
        <Typography variant="subtitle3">
          <strong>{t('common:amldd.lists')}:</strong> {record.listas}
        </Typography>
      </Grid>
    )}

    {record.data && (
      <Grid item xs={12}>
        <Typography variant="caption">
          <strong>{t('common:amldd.date')}:</strong>{' '}
          {new Date(record.data).toLocaleString()}
        </Typography>
      </Grid>
    )}
  </>
)

// MTLSN News (Media monitoring - With news)
export const renderMtlsnNewsFields = (record, t) => (
  <>
    {renderMtlsnFields(record, t)}

    {/* Noticias Section */}
    {record.noticias && Array.isArray(record.noticias) && record.noticias.length > 0 && (
      <Grid item xs={12}>
        <Typography variant="subtitle3" color="primary" sx={{ mb: 1 }}>
          <strong>{t('common:amldd.news')}:</strong>
        </Typography>
        <Box sx={{ pl: 2 }}>
          {record.noticias.map((noticia, idx) => (
            <Paper
              key={idx}
              elevation={0}
              sx={{ mb: 1, p: 1.5, backgroundColor: 'rgba(0,0,0,0.04)' }}
            >
              {noticia.titulo && (
                <Typography variant="body2" fontWeight="bold">
                  {noticia.titulo}
                </Typography>
              )}
              {noticia.conteudo && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {noticia.conteudo}
                </Typography>
              )}
              {noticia.fonte && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {t('common:amldd.source')}: {noticia.fonte}
                </Typography>
              )}
              {noticia.data && (
                <Typography variant="caption" color="text.secondary">
                  {t('common:amldd.date')}: {new Date(noticia.data).toLocaleDateString()}
                </Typography>
              )}
            </Paper>
          ))}
        </Box>
      </Grid>
    )}
  </>
)
